import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Container, Row, Col, Card, Badge, Table, Spinner, Alert, Button, Form, Modal, Collapse
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMoneyBillWave, faHourglassHalf, faSync, faExclamationTriangle, faUpload,
    faChevronDown, faChevronUp, faInfoCircle, faCheckCircle, faPiggyBank, faFileInvoiceDollar
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useMediaQuery } from 'react-responsive';
import api from '../api';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import {
    useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel,
    flexRender, createColumnHelper,
} from '@tanstack/react-table';
import SortableHeader from '../common/SortableHeader';
import { checklistFilterFn, rangeFilterFn } from '../common/clientFilterUtils';

const columnHelper = createColumnHelper();

const euro = (v) => (v === null || v === undefined ? '—' :
    `${Number(v).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
const giorno = (iso, fmt = 'EEEE d MMMM') => (iso ? format(parseISO(iso), fmt, { locale: it }) : '—');
const giornoBreve = (iso) => giorno(iso, 'EEE d/M');

// Finestra di arrivo del bonifico su myPOS: "martedì 20 sera - mercoledì 21 ottobre"
const arrivoTesto = (da, a) => {
    if (!da || !a) return '—';
    return `${giorno(da, 'EEEE d')} sera – ${giorno(a, 'EEEE d MMMM')}`;
};

const STATO_CONFIG = {
    'In Sospeso': { variant: 'warning', icon: faHourglassHalf },
    'Da Liquidare': { variant: 'info', icon: faMoneyBillWave },
    'Liquidato': { variant: 'success', icon: faCheckCircle }
};

const BADGE_FOGLIO = {
    registrato: { bg: 'success', testo: 'Registrato su Firefly' },
    archiviato: { bg: 'secondary', testo: 'Già contabilizzato' },
    importato: { bg: 'warning', testo: 'Da registrare' },
};

function Riga({ label, valore, negativo, forte, nascosta }) {
    if (nascosta) return null;
    return (
        <div className={`d-flex justify-content-between py-1 ${forte ? 'fw-bold border-top mt-1 pt-2' : ''}`}>
            <span className={forte ? '' : 'text-muted'}>{label}</span>
            <span className={negativo ? 'text-danger' : ''}>{negativo && valore > 0 ? '− ' : ''}{euro(Math.abs(valore))}</span>
        </div>
    );
}

// ---------------------------------------------------------------------------------
// Carta "Prossimo bonifico"
// ---------------------------------------------------------------------------------
function ProssimoBonifico({ cassa }) {
    const b = cassa.prossimo_bonifico;
    if (!b) return null;
    const stima = b.tipo === 'stima';
    const slitta = b.esito === 'slitta';
    const origine = b.tipo === 'foglio' ? 'Dal foglio Nuvei' : (stima ? 'Stima' : 'Calcolato dal netto IBS');

    return (
        <Card className={`border-0 shadow-sm mb-4 ${slitta ? 'border-start border-warning border-4' : ''}`}>
            <Card.Body>
                <Row className="align-items-center g-3">
                    <Col lg={6}>
                        <div className="text-muted fw-medium">
                            <FontAwesomeIcon icon={faPiggyBank} className="me-2" />
                            Prossimo bonifico su myPOS
                            <Badge bg={b.tipo === 'foglio' ? 'success' : 'secondary'} className="ms-2">{origine}</Badge>
                        </div>
                        <div className="fw-bold" style={{ fontSize: '2.4rem', lineHeight: 1.1 }}>
                            {slitta ? <span className="text-warning">Non parte</span> : <>{stima ? '≈ ' : ''}{euro(b.bonifico)}</>}
                        </div>
                        <div className="mt-1">
                            {slitta
                                ? <>Saldo di <strong>{euro(b.saldo)}</strong> sotto i {cassa.soglia} €: slitta al ciclo successivo (si risparmiano {euro(cassa.fee_bonifico)}).</>
                                : <>Arriva <strong>{arrivoTesto(b.arrivo_da, b.arrivo_a)}</strong></>}
                        </div>
                        {stima && !slitta && b.bonifico_min !== undefined && (
                            <small className="text-muted">Intervallo probabile {euro(b.bonifico_min)} – {euro(b.bonifico_max)}</small>
                        )}
                        {slitta && b.mancano > 0 && (
                            <Alert variant="warning" className="mt-3 mb-0 py-2">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                Mancano <strong>{euro(b.mancano)}</strong> per superare la soglia dei {cassa.soglia} €. Il netto resta in attesa e si somma alla settimana dopo.
                            </Alert>
                        )}
                    </Col>
                    <Col lg={6}>
                        <div className="small text-muted mb-1">
                            Ciclo IBS di venerdì {giorno(b.ciclo_data, 'd MMMM')}
                            {b.n_ordini ? ` · ${b.n_ordini} ordini` : ''}
                        </div>
                        <Riga label="Netto IBS (dopo commissioni, IVA e rimborsi)" valore={b.netto_ibs} />
                        <Riga label="Riporto della settimana prima" valore={b.riporto_in} nascosta={!b.riporto_in} />
                        <Riga label="Fee Nuvei 0,75% sugli incassi" valore={b.fee_nuvei} negativo />
                        <Riga label="Costo bonifico" valore={b.fee_bonifico} negativo nascosta={!b.fee_bonifico} />
                        <Riga label={slitta ? 'Resta in attesa' : 'Arriva su myPOS'} valore={slitta ? b.saldo : b.bonifico} forte />
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
}

// ---------------------------------------------------------------------------------
// Modale foglio Nuvei
// ---------------------------------------------------------------------------------
function ModaleFoglioNuvei({ show, onHide, anteprimaIniziale, onFatto }) {
    const [anteprima, setAnteprima] = useState(null);
    const [caricamento, setCaricamento] = useState(false);
    const [errore, setErrore] = useState(null);
    const [dataAccredito, setDataAccredito] = useState('');

    useEffect(() => {
        if (show) {
            setAnteprima(anteprimaIniziale || null);
            setErrore(null);
        }
    }, [show, anteprimaIniziale]);

    useEffect(() => {
        if (anteprima?.arrivo_a) setDataAccredito(anteprima.arrivo_a);
    }, [anteprima]);

    const caricaFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setCaricamento(true);
        setErrore(null);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await api.post('/mirakl/payments/nuvei/importa', fd);
            setAnteprima(res.data);
            onFatto(false);
        } catch (err) {
            setErrore(err.response?.data?.detail || 'Impossibile leggere il file');
        } finally {
            setCaricamento(false);
        }
    };

    const azione = async (percorso, metodo, body) => {
        setCaricamento(true);
        setErrore(null);
        try {
            const res = await api({ method: metodo, url: `/mirakl/payments/nuvei/${anteprima.id}${percorso}`, data: body });
            if (metodo === 'delete') {
                toast.success('Foglio eliminato');
                onFatto(true);
                return;
            }
            setAnteprima(res.data);
            toast.success(percorso === '/registra' ? 'Movimenti registrati su Firefly' : 'Foglio segnato come già contabilizzato');
            onFatto(false);
        } catch (err) {
            setErrore(err.response?.data?.detail || 'Operazione non riuscita');
        } finally {
            setCaricamento(false);
        }
    };

    const a = anteprima;
    const imp = a?.importi;
    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title className="fs-5"><FontAwesomeIcon icon={faFileInvoiceDollar} className="me-2" />Foglio di liquidazione Nuvei</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {errore && <Alert variant="danger" dismissible onClose={() => setErrore(null)}>{errore}</Alert>}
                {!a && (
                    <div>
                        <p className="text-muted">
                            I fogli Nuvei che arrivano ogni lunedì a modenacinema@gmail.com vengono letti e registrati in automatico.
                            Usa questo caricamento solo come riserva: carica il file <strong>SettlementInvoice…xls</strong> e prima di registrare vedrai l'anteprima.
                        </p>
                        <Form.Control type="file" accept=".xls,.xlsx" onChange={caricaFile} disabled={caricamento} />
                        {caricamento && <div className="mt-3"><Spinner size="sm" animation="border" className="me-2" />Lettura in corso…</div>}
                    </div>
                )}
                {a && (
                    <>
                        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                            <div>
                                <div className="fw-bold">Fattura {a.numero_fattura}</div>
                                <small className="text-muted">
                                    Periodo {a.periodo_da ? giorno(a.periodo_da, 'd/M') : ''} – {a.periodo_a ? giorno(a.periodo_a, 'd/M/yyyy') : ''}
                                    {' · '}ciclo IBS del {giorno(a.ciclo_ibs_data, 'd MMMM')}
                                </small>
                            </div>
                            <Badge bg={(BADGE_FOGLIO[a.stato] || {}).bg || 'secondary'}>{(BADGE_FOGLIO[a.stato] || {}).testo || a.stato}</Badge>
                        </div>

                        <Row className="g-3">
                            <Col md={6}>
                                <Card className="h-100"><Card.Body>
                                    <Riga label="Incassi (carte e PayPal)" valore={imp.pagamenti_lordi} />
                                    <Riga label="Rimborsi" valore={imp.rimborsi} negativo />
                                    <Riga label="Commissioni IBS + IVA" valore={imp.commissioni_ibs} negativo />
                                    <Riga label="Fee Nuvei 0,75%" valore={imp.fee_nuvei} negativo />
                                    <Riga label="Netto della settimana" valore={imp.netto_settimana} forte />
                                    <Riga label="Riporto settimana prima" valore={imp.riporto_precedente} nascosta={!imp.riporto_precedente} />
                                    <Riga label="Costo bonifico" valore={imp.fee_bonifico} negativo nascosta={!imp.fee_bonifico} />
                                    <Riga label={a.esito === 'paga' ? 'Bonifico su myPOS' : 'Riportato alla settimana dopo'}
                                        valore={a.esito === 'paga' ? imp.bonifico_totale : imp.riportato_dopo} forte />
                                </Card.Body></Card>
                            </Col>
                            <Col md={6}>
                                <Card className="h-100"><Card.Body>
                                    <div className="fw-medium mb-2">Controlli</div>
                                    {a.verifica_ibs.disponibile ? (
                                        a.verifica_ibs.ok
                                            ? <div className="text-success mb-2"><FontAwesomeIcon icon={faCheckCircle} className="me-1" />Il netto coincide con quello del ciclo IBS ({euro(a.verifica_ibs.netto_ibs_foglio)})</div>
                                            : <div className="text-danger mb-2"><FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />Differenza con il netto IBS: foglio {euro(a.verifica_ibs.netto_ibs_foglio)} contro nostro {euro(a.verifica_ibs.netto_ibs_nostro)} ({euro(a.verifica_ibs.differenza)})</div>
                                    ) : (
                                        <div className="text-muted mb-2"><FontAwesomeIcon icon={faInfoCircle} className="me-1" />Ciclo IBS non ancora presente nei nostri dati: sincronizza e riapri.</div>
                                    )}
                                    <div className="mb-2">
                                        Incassi collegati a un ordine: <strong>{a.ordini.collegate}</strong> su {a.ordini.righe}
                                    </div>
                                    <div className="mb-2">
                                        Arrivo previsto: <strong>{arrivoTesto(a.arrivo_da, a.arrivo_a)}</strong>
                                    </div>
                                    {a.note && <Alert variant="warning" className="py-2 mb-0">{a.note}</Alert>}
                                </Card.Body></Card>
                            </Col>
                        </Row>

                        <div className="fw-medium mt-4 mb-2">Movimenti che verranno creati su Firefly</div>
                        {a.movimenti.length === 0 ? (
                            <div className="text-muted">Nessun movimento.</div>
                        ) : (
                            <Table size="sm" responsive className="mb-2">
                                <thead><tr><th>Operazione</th><th>Da</th><th>A</th><th className="text-end">Importo</th></tr></thead>
                                <tbody>
                                    {a.movimenti.map((m, i) => (
                                        <tr key={i}>
                                            <td>{m.tipo === 'giroconto' ? 'Giroconto' : 'Spesa'} <small className="text-muted d-block">{m.descrizione}</small></td>
                                            <td>{m.da || '—'}</td>
                                            <td>{m.a || '—'}</td>
                                            <td className="text-end">{euro(m.importo)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                        {a.problemi.map((p, i) => <Alert key={i} variant="danger" className="py-2 mb-2">{p}</Alert>)}

                        {a.stato === 'importato' && (
                            <Form.Group className="mt-3" style={{ maxWidth: 260 }}>
                                <Form.Label className="small text-muted">Data da usare su Firefly (arrivo su myPOS)</Form.Label>
                                <Form.Control type="date" value={dataAccredito} onChange={(e) => setDataAccredito(e.target.value)} />
                            </Form.Group>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer className="justify-content-between">
                <div>
                    {a && a.stato === 'importato' && (
                        <Button variant="outline-danger" size="sm" disabled={caricamento}
                            onClick={() => window.confirm('Eliminare questo foglio importato?') && azione('', 'delete')}>
                            Elimina
                        </Button>
                    )}
                </div>
                <div className="d-flex gap-2">
                    {a && a.stato === 'importato' && (
                        <>
                            <Button variant="outline-secondary" disabled={caricamento}
                                onClick={() => window.confirm("Segnare come già contabilizzato a mano? Non verrà creato nulla su Firefly.") && azione('/archivia', 'post')}>
                                Già contabilizzato a mano
                            </Button>
                            <Button variant="success" disabled={caricamento || a.problemi.length > 0 || !dataAccredito}
                                onClick={() => azione('/registra', 'post', { data_accredito: dataAccredito })}>
                                {caricamento ? <Spinner size="sm" animation="border" /> : 'Registra su Firefly'}
                            </Button>
                        </>
                    )}
                    <Button variant="light" onClick={onHide}>Chiudi</Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}

// ---------------------------------------------------------------------------------
// Dettaglio righe contabili (tabella completa, caricata solo quando serve)
// ---------------------------------------------------------------------------------
function DettaglioTransazioni() {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sorting, setSorting] = useState(() => {
        const saved = sessionStorage.getItem('list-sorting-ibs-payments');
        return saved ? JSON.parse(saved) : [{ id: 'transaction_date', desc: true }];
    });
    const [columnFilters, setColumnFilters] = useState(() => {
        const saved = sessionStorage.getItem('list-filters-ibs-payments');
        return saved ? JSON.parse(saved) : [];
    });

    useEffect(() => { sessionStorage.setItem('list-sorting-ibs-payments', JSON.stringify(sorting)); }, [sorting]);
    useEffect(() => { sessionStorage.setItem('list-filters-ibs-payments', JSON.stringify(columnFilters)); }, [columnFilters]);

    useEffect(() => {
        api.get('/mirakl/payments/transactions', { params: { limit: 500 } })
            .then(r => setTransactions(r.data))
            .catch(err => console.error('Errore caricamento transazioni:', err))
            .finally(() => setLoading(false));
    }, []);

    const columns = useMemo(() => [
        columnHelper.accessor('order_id', {
            header: 'ID Ordine',
            cell: info => <code className="text-primary">{info.getValue() || '—'}</code>,
            meta: { filterVariant: 'text' },
        }),
        columnHelper.accessor('transaction_number', {
            header: 'N. Transazione',
            cell: info => <small className="text-muted">{info.getValue()}</small>,
            meta: { filterVariant: 'text' },
        }),
        columnHelper.accessor('stato_pagamento', {
            header: 'Stato',
            filterFn: checklistFilterFn,
            meta: { filterVariant: 'select', filterOptions: Object.keys(STATO_CONFIG) },
            cell: info => {
                const stato = info.getValue();
                const cfg = STATO_CONFIG[stato] || { variant: 'secondary', icon: faInfoCircle };
                return <Badge bg={cfg.variant}><FontAwesomeIcon icon={cfg.icon} className="me-1" />{stato}</Badge>;
            },
        }),
        columnHelper.accessor('amount_credited', {
            header: 'Importo',
            filterFn: rangeFilterFn,
            meta: { filterVariant: 'number-range' },
            cell: info => <span className="fw-semibold text-success">{euro(info.getValue() || 0)}</span>,
        }),
        columnHelper.accessor('amount_debited', {
            header: 'Commissione',
            filterFn: rangeFilterFn,
            meta: { filterVariant: 'number-range' },
            cell: info => {
                const v = info.getValue();
                return <span className="text-danger">{v > 0 && '- '}{euro(v || 0)}</span>;
            },
        }),
        columnHelper.accessor('transaction_date', {
            header: 'Data Transazione',
            filterFn: rangeFilterFn,
            meta: { filterVariant: 'date-range' },
            cell: info => <small>{info.getValue() ? format(parseISO(info.getValue()), 'dd/MM/yyyy HH:mm') : '—'}</small>,
        }),
        columnHelper.accessor('accounting_document_date', {
            header: 'Data Ciclo',
            filterFn: rangeFilterFn,
            meta: { filterVariant: 'date-range' },
            cell: info => <small className="text-muted">{info.getValue() ? format(parseISO(info.getValue()), 'dd/MM/yyyy') : '—'}</small>,
        }),
    ], []);

    const table = useReactTable({
        data: transactions,
        columns,
        state: { sorting, columnFilters },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    if (loading) return <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>;
    if (transactions.length === 0) return <div className="text-center py-4 text-muted">Nessuna transazione trovata</div>;

    return (
        <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
            {columnFilters.length > 0 && (
                <div className="p-2">
                    <Button variant="outline-secondary" size="sm" onClick={() => table.resetColumnFilters()}>
                        Azzera filtri <Badge bg="danger" className="ms-1">{columnFilters.length}</Badge>
                    </Button>
                </div>
            )}
            <Table hover className="mb-0">
                <thead className="table-light">
                    {table.getHeaderGroups().map(hg => (
                        <tr key={hg.id}>
                            {hg.headers.map(header => (
                                <SortableHeader key={header.id} header={header} table={table} stickyColumns={[]} />
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.length === 0 ? (
                        <tr><td colSpan={columns.length} className="text-center text-muted py-4">Nessuna transazione corrisponde ai filtri</td></tr>
                    ) : (
                        table.getRowModel().rows.map(row => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map(cell => (
                                    <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                                ))}
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>
        </div>
    );
}

// ---------------------------------------------------------------------------------
// Pagina
// ---------------------------------------------------------------------------------
const IBSPaymentsDashboard = () => {
    const isMobile = useMediaQuery({ maxWidth: 767 });
    const [cassa, setCassa] = useState(null);
    const [fogli, setFogli] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState(null);
    const [mostraSospesi, setMostraSospesi] = useState(false);
    const [mostraDettaglio, setMostraDettaglio] = useState(false);
    const [modale, setModale] = useState({ show: false, anteprima: null });
    const [gmail, setGmail] = useState(null);

    const carica = useCallback(async () => {
        try {
            const [c, f] = await Promise.all([
                api.get('/mirakl/payments/cassa'),
                api.get('/mirakl/payments/nuvei'),
            ]);
            setCassa(c.data);
            setFogli(f.data);
            setError(null);
        } catch (err) {
            console.error('Errore caricamento cassa IBS:', err);
            setError('Errore nel caricamento dei dati di cassa');
        }
    }, []);

    useEffect(() => {
        setLoading(true);
        carica().finally(() => setLoading(false));
        api.get('/mirakl/payments/nuvei-gmail/stato').then(r => setGmail(r.data)).catch(() => setGmail(null));
    }, [carica]);

    const sincronizza = async () => {
        setSyncing(true);
        try {
            const r = await api.post('/mirakl/payments/sync', null, { params: { days_back: 30 } });
            const s = r.data.stats;
            toast.success(`Sincronizzazione completata: ${s.created} nuove, ${s.updated} aggiornate${s.riallineate ? `, ${s.riallineate} ordini riallineati` : ''}`);
            await carica();
        } catch (err) {
            toast.error('Errore durante la sincronizzazione pagamenti');
        } finally {
            setSyncing(false);
        }
    };

    const apriFoglio = async (id) => {
        try {
            const r = await api.get(`/mirakl/payments/nuvei/${id}`);
            setModale({ show: true, anteprima: r.data });
        } catch (err) {
            toast.error('Impossibile aprire il foglio');
        }
    };

    if (loading) {
        return (
            <Container fluid className="py-4 text-center">
                <Spinner animation="border" variant="primary" />
                <div className="mt-3 text-muted">Caricamento cassa IBS…</div>
            </Container>
        );
    }
    if (error || !cassa) {
        return (
            <Container fluid className="py-4">
                <Alert variant="danger"><FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />{error}</Alert>
            </Container>
        );
    }

    const na = cassa.non_allineati;
    const prossimi = cassa.bonifici;

    return (
        <Container fluid className="py-3">
            <Row className="mb-3 g-2 align-items-center">
                <Col xs={12} md>
                    <h2 className="mb-1 h3">Cassa IBS / Feltrinelli</h2>
                    <p className="text-muted mb-0 small">
                        Quanto è in sospeso e quando arriva il bonifico su myPOS · ciclo IBS ogni venerdì, bonifico martedì sera / mercoledì · il foglio Nuvei si legge da solo dalla posta
                    </p>
                </Col>
                <Col xs={12} md="auto" className="d-flex flex-wrap gap-2">
                    <Button variant="outline-primary" size="sm" onClick={() => setModale({ show: true, anteprima: null })}>
                        <FontAwesomeIcon icon={faUpload} className="me-2" />Carica foglio Nuvei
                    </Button>
                    <Button variant="primary" size="sm" onClick={sincronizza} disabled={syncing}>
                        <FontAwesomeIcon icon={faSync} spin={syncing} className="me-2" />
                        {syncing ? 'Sincronizzazione…' : 'Sincronizza'}
                    </Button>
                </Col>
            </Row>

            {gmail && !gmail.ok && (
                <Alert variant="info" className="py-2">
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    Lettura automatica dei fogli Nuvei dalla posta non attiva ({gmail.messaggio}).{' '}
                    <a href={`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/auth/google/login?purpose=app_auth`}>
                        Autorizza Google
                    </a>{' '}(serve una volta sola). Nel frattempo puoi caricare il foglio a mano.
                </Alert>
            )}
            {cassa.fogli_mancanti.length > 0 && (
                <Alert variant="warning" className="py-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    Manca il foglio Nuvei del ciclo di venerdì {cassa.fogli_mancanti.map(d => giorno(d, 'd/M')).join(', ')}: carica il file arrivato per mail per confermare il bonifico.
                </Alert>
            )}
            {na.n_non_allineati > 0 && (
                <Alert variant="warning" className="py-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <strong>{na.n_non_allineati} ordini</strong> ({euro(na.importo_non_allineato)}) non vengono aggiornati da Mirakl da giorni e sono esclusi dai totali.
                    Premi <strong>Sincronizza</strong> per riallinearli.
                </Alert>
            )}

            <ProssimoBonifico cassa={cassa} />

            <Row className="g-2 g-md-3 mb-4">
                <Col md={4}>
                    <Card className="h-100 border-0 shadow-sm"><Card.Body className={isMobile ? "py-2" : ""}>
                        <div className="text-muted small"><FontAwesomeIcon icon={faHourglassHalf} className="me-1 text-warning" />In sospeso</div>
                        <div className={`fw-bold text-warning ${isMobile ? "fs-4" : "fs-3"}`}>{euro(cassa.in_sospeso.importo)}</div>
                        <div className="small text-muted">{cassa.in_sospeso.n_ordini} ordini · il cliente non ha ancora confermato (di solito dopo circa {cassa.in_sospeso.mediana_giorni_sblocco} giorni dall'ordine)</div>
                    </Card.Body></Card>
                </Col>
                <Col md={4}>
                    <Card className="h-100 border-0 shadow-sm"><Card.Body className={isMobile ? "py-2" : ""}>
                        <div className="text-muted small"><FontAwesomeIcon icon={faMoneyBillWave} className="me-1 text-info" />Pagabile ora</div>
                        <div className={`fw-bold text-info ${isMobile ? "fs-4" : "fs-3"}`}>{euro(cassa.pagabile.importo)}</div>
                        <div className="small text-muted">{cassa.pagabile.n_ordini} ordini · entrano nel ciclo di venerdì {giorno(cassa.prossimo_ciclo, 'd/M')}</div>
                    </Card.Body></Card>
                </Col>
                <Col md={4}>
                    <Card className="h-100 border-0 shadow-sm"><Card.Body className={isMobile ? "py-2" : ""}>
                        <div className="text-muted small"><FontAwesomeIcon icon={faPiggyBank} className="me-1 text-secondary" />Riporto Nuvei</div>
                        <div className={`fw-bold ${isMobile ? "fs-4" : "fs-3"}`}>{euro(cassa.riporto_nuvei)}</div>
                        <div className="small text-muted">Già incassato da Nuvei ma sotto i {cassa.soglia} €: si somma al prossimo bonifico</div>
                    </Card.Body></Card>
                </Col>
            </Row>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white"><h5 className="mb-0">Prossimi bonifici</h5></Card.Header>
                <Card.Body className="p-0">
                    {isMobile ? (
                        <div>
                            {prossimi.map((b, i) => {
                                const paga = b.esito === 'paga';
                                const approx = b.tipo === 'stima' ? '≈ ' : '';
                                return (
                                    <div key={i} className="px-3 py-2 border-bottom">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div>
                                                <div className="fw-semibold">{giornoBreve(b.arrivo_da)} sera – {giornoBreve(b.arrivo_a)}</div>
                                                <div className="small text-muted">ciclo IBS ven {giorno(b.ciclo_data, 'd/M')}</div>
                                            </div>
                                            <div className="text-end">
                                                <div className="fw-bold fs-5">{paga ? <>{approx}{euro(b.bonifico)}</> : '—'}</div>
                                                {paga
                                                    ? <Badge bg="success">Paga</Badge>
                                                    : <Badge bg="warning" text="dark">Slitta</Badge>}
                                                {b.tipo === 'foglio' && <Badge bg="light" text="dark" className="ms-1">dal foglio</Badge>}
                                            </div>
                                        </div>
                                        <div className="small text-muted mt-1">
                                            Netto IBS {approx}{euro(b.netto_ibs)} · fee {euro((b.fee_nuvei || 0) + (paga ? (b.fee_bonifico || 0) : 0))}
                                            {b.tipo === 'stima' && paga && b.bonifico_min !== undefined && <> · tra {euro(b.bonifico_min)} e {euro(b.bonifico_max)}</>}
                                            {b.tipo === 'stima' && b.probabilita_pagamento !== undefined && b.probabilita_pagamento < 1 && <> · {Math.round(b.probabilita_pagamento * 100)}% sopra soglia</>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                    <Table responsive hover className="mb-0 align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>Arrivo su myPOS</th>
                                <th>Ciclo IBS</th>
                                <th className="text-end">Netto IBS</th>
                                <th className="text-end">Fee</th>
                                <th className="text-end">Bonifico</th>
                                <th>Esito</th>
                            </tr>
                        </thead>
                        <tbody>
                            {prossimi.map((b, i) => (
                                <tr key={i}>
                                    <td className="text-nowrap">{giornoBreve(b.arrivo_da)} sera – {giornoBreve(b.arrivo_a)}</td>
                                    <td className="text-nowrap">ven {giorno(b.ciclo_data, 'd/M')}</td>
                                    <td className="text-end">{b.tipo === 'stima' ? '≈ ' : ''}{euro(b.netto_ibs)}</td>
                                    <td className="text-end text-muted">{euro((b.fee_nuvei || 0) + (b.esito === 'paga' ? (b.fee_bonifico || 0) : 0))}</td>
                                    <td className="text-end fw-semibold">
                                        {b.esito === 'paga' ? <>{b.tipo === 'stima' ? '≈ ' : ''}{euro(b.bonifico)}</> : '—'}
                                        {b.tipo === 'stima' && b.esito === 'paga' && b.bonifico_min !== undefined && (
                                            <div className="small text-muted fw-normal">{euro(b.bonifico_min)} – {euro(b.bonifico_max)}</div>
                                        )}
                                    </td>
                                    <td>
                                        {b.esito === 'paga'
                                            ? <Badge bg="success">Paga</Badge>
                                            : <Badge bg="warning" text="dark">Slitta</Badge>}
                                        {b.tipo === 'stima' && b.probabilita_pagamento !== undefined && b.probabilita_pagamento < 1 && (
                                            <small className="text-muted ms-2">{Math.round(b.probabilita_pagamento * 100)}% sopra soglia</small>
                                        )}
                                        {b.tipo === 'foglio' && <Badge bg="light" text="dark" className="ms-2">dal foglio</Badge>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    )}
                    <div className="small text-muted p-3 border-top">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                        Le stime considerano solo gli ordini già presenti (in sospeso e pagabili): i nuovi ordini aumenteranno gli importi.
                        Il bonifico parte solo se riporto + netto − fee supera {cassa.soglia} €; altrimenti slitta di una settimana e non si pagano i {euro(cassa.fee_bonifico)}.
                    </div>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white d-flex justify-content-between align-items-center"
                    role="button" onClick={() => setMostraSospesi(!mostraSospesi)}>
                    <h5 className="mb-0">Ordini in sospeso ({cassa.in_sospeso.n_ordini})</h5>
                    <FontAwesomeIcon icon={mostraSospesi ? faChevronUp : faChevronDown} />
                </Card.Header>
                <Collapse in={mostraSospesi}>
                    <div>
                        {isMobile ? (
                            <div>
                                {cassa.in_sospeso.ordini.map(o => (
                                    <div key={o.order_id} className="px-3 py-2 border-bottom">
                                        <div className="d-flex justify-content-between">
                                            <code className="text-primary">{o.order_id}</code>
                                            <span className="fw-semibold">{euro(o.netto)}</span>
                                        </div>
                                        <div className="small text-muted">
                                            creato {o.creato ? giorno(o.creato, 'd/M') : '—'}{o.giorni != null ? ` (${o.giorni} gg fa)` : ''}
                                            {' · '}si sblocca {o.ciclo_stimato ? `ven ${giorno(o.ciclo_stimato, 'd/M')}` : '—'}
                                            {o.arrivo_stimato ? ` · bonifico ${giornoBreve(o.arrivo_stimato)}` : ''}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                        <Table responsive hover size="sm" className="mb-0 align-middle">
                            <thead className="table-light">
                                <tr><th>Ordine</th><th>Creato</th><th className="text-end">Giorni</th><th className="text-end">Netto</th><th>Si sblocca (ciclo)</th><th>Bonifico stimato</th></tr>
                            </thead>
                            <tbody>
                                {cassa.in_sospeso.ordini.map(o => (
                                    <tr key={o.order_id}>
                                        <td><code className="text-primary">{o.order_id}</code></td>
                                        <td className="text-nowrap">{o.creato ? giorno(o.creato, 'd/M') : '—'}</td>
                                        <td className="text-end">{o.giorni ?? '—'}</td>
                                        <td className="text-end">{euro(o.netto)}</td>
                                        <td className="text-nowrap">{o.ciclo_stimato ? `ven ${giorno(o.ciclo_stimato, 'd/M')}` : '—'}</td>
                                        <td className="text-nowrap">{o.arrivo_stimato ? giornoBreve(o.arrivo_stimato) : '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        )}
                    </div>
                </Collapse>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white"><h5 className="mb-0">Storico bonifici (fogli Nuvei)</h5></Card.Header>
                <Card.Body className="p-0">
                    {fogli.length === 0 ? (
                        <div className="text-center text-muted py-4">Nessun foglio caricato</div>
                    ) : (
                        isMobile ? (
                            <div>
                                {fogli.map(f => {
                                    const cfg = BADGE_FOGLIO[f.stato] || { bg: 'secondary', testo: f.stato };
                                    return (
                                        <div key={f.id} className="px-3 py-2 border-bottom">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <code>{f.numero_fattura}</code>
                                                    <div className="small text-muted">
                                                        {f.data_fattura ? giorno(f.data_fattura, 'd/M/yyyy') : ''}
                                                        {f.ciclo_ibs_data ? ` · ciclo ven ${giorno(f.ciclo_ibs_data, 'd/M')}` : ''}
                                                    </div>
                                                </div>
                                                <div className="text-end">
                                                    <div className="fw-bold">{f.bonifico_totale > 0 ? euro(f.bonifico_totale) : '—'}</div>
                                                    <Badge bg={cfg.bg}>{cfg.testo}</Badge>
                                                </div>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center mt-1">
                                                <span className="small text-muted">
                                                    netto settimana {euro(f.netto_settimana)}{f.riportato_dopo > 0 ? ` · riportato ${euro(f.riportato_dopo)}` : ''}
                                                </span>
                                                <Button size="sm" variant="outline-secondary" onClick={() => apriFoglio(f.id)}>Apri</Button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                        <Table responsive hover className="mb-0 align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th>Fattura</th><th>Ciclo IBS</th><th className="text-end">Netto settimana</th>
                                    <th className="text-end">Bonifico</th><th className="text-end">Riportato</th><th>Stato</th><th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {fogli.map(f => {
                                    const cfg = BADGE_FOGLIO[f.stato] || { bg: 'secondary', testo: f.stato };
                                    return (
                                        <tr key={f.id}>
                                            <td><code>{f.numero_fattura}</code><div className="small text-muted">{f.data_fattura ? giorno(f.data_fattura, 'd/M/yyyy') : ''}</div></td>
                                            <td className="text-nowrap">{f.ciclo_ibs_data ? `ven ${giorno(f.ciclo_ibs_data, 'd/M')}` : '—'}</td>
                                            <td className="text-end">{euro(f.netto_settimana)}</td>
                                            <td className="text-end fw-semibold">{f.bonifico_totale > 0 ? euro(f.bonifico_totale) : '—'}</td>
                                            <td className="text-end">{f.riportato_dopo > 0 ? euro(f.riportato_dopo) : '—'}</td>
                                            <td><Badge bg={cfg.bg}>{cfg.testo}</Badge></td>
                                            <td className="text-end"><Button size="sm" variant="outline-secondary" onClick={() => apriFoglio(f.id)}>Apri</Button></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                        )
                    )}
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white d-flex justify-content-between align-items-center"
                    role="button" onClick={() => setMostraDettaglio(!mostraDettaglio)}>
                    <h5 className="mb-0">Dettaglio righe contabili IBS</h5>
                    <FontAwesomeIcon icon={mostraDettaglio ? faChevronUp : faChevronDown} />
                </Card.Header>
                {mostraDettaglio && <Card.Body className="p-0"><DettaglioTransazioni /></Card.Body>}
            </Card>

            <div className="small text-muted mt-3">Ultima sincronizzazione con Mirakl: {cassa.ultima_sync ? format(parseISO(cassa.ultima_sync), 'dd/MM/yyyy HH:mm') : '—'} (automatica ogni 6 ore)</div>

            <ModaleFoglioNuvei
                show={modale.show}
                anteprimaIniziale={modale.anteprima}
                onHide={() => setModale({ show: false, anteprima: null })}
                onFatto={(chiudi) => { carica(); if (chiudi) setModale({ show: false, anteprima: null }); }}
            />
        </Container>
    );
};

export default IBSPaymentsDashboard;
